import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u3npv8l97n3eas3gpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试。';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请确保填写内容符合规范。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '番号查询与解析完全指南',
    url: 'https://formatguide.github.io',
    description: '专注于番号的全面解析，涵盖番号的定义、分类、查询方法及常见格式说明。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://formatguide.github.io/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': 'https://formatguide.github.io/about', name: '关于我们' } },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6 w-full">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6">
              关于我们
            </h1>

            {/* Brand Intro */}
            <section className="mb-16">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 md:p-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  <strong>番号查询与解析完全指南</strong> 成立于 2024 年，是国内最专业的番号知识平台之一。我们致力于为用户提供全面、准确、权威的番号查询和解析服务，帮助用户快速掌握番号体系的各项知识。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  无论你是初次接触番号的新手，还是希望深入了解番号体系的老用户，我们的内容都能满足你的需求。从番号的基本结构、厂商系列解读，到进阶的查询方法和跨厂商对比分析，本站覆盖了番号知识的方方面面。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们的内容团队由多位资深番号研究者组成，所有文章都经过严格审核，确保信息的准确性和时效性。我们持续关注番号体系的最新变化，及时更新内容，为用户提供最前沿的番号知识。
                </p>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-16">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                信誉与口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                    内容权威
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    所有内容均经过多方交叉验证，确保信息的准确性和权威性，让用户查阅无忧。
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-refresh-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                    持续更新
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    紧跟番号体系变化，及时更新文章内容，确保用户获取的信息始终是最新的。
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-user-heart-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                    用户至上
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    以用户体验为核心，内容编排清晰易懂，从入门到进阶满足不同层次用户需求。
                  </p>
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="mb-16">
              <div className="bg-background-100 rounded-xl p-8 md:p-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-primary-600 font-heading mb-1">
                      50+
                    </div>
                    <p className="text-xs text-foreground-500">番号前缀收录</p>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-accent-600 font-heading mb-1">
                      15+
                    </div>
                    <p className="text-xs text-foreground-500">深度解析文章</p>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-secondary-600 font-heading mb-1">
                      6
                    </div>
                    <p className="text-xs text-foreground-500">知识分类板块</p>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-primary-600 font-heading mb-1">
                      100%
                    </div>
                    <p className="text-xs text-foreground-500">内容免费开放</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact Form */}
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 md:p-10 max-w-2xl">
                <p className="text-sm text-foreground-600 mb-6">
                  如果你有任何关于番号查询的问题、建议或合作意向，欢迎通过以下表单与我们联系。我们会在 48 小时内回复。
                </p>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5"
                >
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      你的称呼 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      电子邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="请输入你的电子邮箱"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer"
                    >
                      <option value="general">一般咨询</option>
                      <option value="suggestion">内容建议</option>
                      <option value="correction">内容纠错</option>
                      <option value="cooperation">合作意向</option>
                      <option value="other">其他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="请输入你的留言内容（最多500字）"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多 500 个字符</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </>
                    ) : (
                      <>
                        发送留言
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm flex items-start gap-2">
                      <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                      {formMessage}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-800 text-sm flex items-start gap-2">
                      <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                      {formMessage}
                    </div>
                  )}
                </form>
              </div>
            </section>

            {/* Quick Links */}
            <section className="mt-16 pt-10 border-t border-background-200/70">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                快速导航
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="text-sm px-4 py-2 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
                <Link
                  to="/category/format-guide"
                  className="text-sm px-4 py-2 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  番号格式指南
                </Link>
                <Link
                  to="/category/manufacturer-series"
                  className="text-sm px-4 py-2 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  厂商系列番号
                </Link>
                <Link
                  to="/category/search-methods"
                  className="text-sm px-4 py-2 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  番号查询方法
                </Link>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}